import { useState } from 'react'
import { toast } from 'sonner'
import { Modal } from './Modal.js'
import { CopyButton } from './CopyButton.js'
import { AdminApiError } from '../adminApi.js'
import { useBranches } from '../hooks/useBranches.js'
import {
  useCreateStaff,
  usePatchStaff,
  useResetStaffPassword,
  useStaff,
} from '../hooks/useStaff.js'
import { fmtDate } from '../format.js'
import type { StaffRow } from '../types.js'

/**
 * Staff tab under tenant detail. List, create (with a branch picker), inline
 * edit of name + branch, and a reset-password action that surfaces the
 * generated temporary password exactly once.
 *
 * Note: there is no staff active/inactive toggle — `users` has no is_active
 * column in V1 (deferred; see CLAUDE.md).
 */
export function TenantStaff({ tenantId }: { tenantId: string }) {
  // Only non-archived branches can take new/edited staff assignments.
  const branches = useBranches(tenantId, false)
  const staff = useStaff(tenantId)
  const createStaff = useCreateStaff(tenantId)
  const patchStaff = usePatchStaff(tenantId)
  const resetPassword = useResetStaffPassword(tenantId)

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({
    email: '',
    display_name: '',
    password: '',
    branch_id: '',
  })

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editBranch, setEditBranch] = useState('')

  // Result of a reset — { staff, temporary_password }, shown once in a modal.
  const [resetResult, setResetResult] = useState<{
    name: string
    password: string
  } | null>(null)

  const branchOptions = branches.data?.branches ?? []
  const branchName = (id: string | null) =>
    branchOptions.find((b) => b.id === id)?.name ?? '—'

  function handleCreate() {
    const email = form.email.trim().toLowerCase()
    const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
    if (
      !looksLikeEmail ||
      !form.display_name.trim() ||
      form.password.length < 12 ||
      !form.branch_id
    ) {
      toast.error(
        'Fill every field — email must be valid, password at least 12 characters.',
      )
      return
    }
    createStaff.mutate(
      {
        email,
        display_name: form.display_name.trim(),
        password: form.password,
        branch_id: form.branch_id,
      },
      {
        onSuccess: () => {
          toast.success('Staff member created.')
          setCreateOpen(false)
          setForm({
            email: '',
            display_name: '',
            password: '',
            branch_id: '',
          })
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not create staff.',
          ),
      },
    )
  }

  function startEdit(row: StaffRow) {
    setEditingId(row.id)
    setEditName(row.display_name)
    setEditBranch(row.branch_id ?? '')
  }

  function saveEdit() {
    if (!editingId) return
    patchStaff.mutate(
      {
        staffId: editingId,
        display_name: editName.trim(),
        ...(editBranch ? { branch_id: editBranch } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Staff updated.')
          setEditingId(null)
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not update staff.',
          ),
      },
    )
  }

  function handleReset(row: StaffRow) {
    resetPassword.mutate(row.id, {
      onSuccess: (res) => {
        setResetResult({
          name: row.display_name,
          password: res.temporary_password,
        })
      },
      onError: (err) =>
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : 'Could not reset password.',
        ),
    })
  }

  const rows = staff.data?.staff ?? []

  const noBranches = !branches.isLoading && branchOptions.length === 0

  return (
    <div>
      <div className="admin-page-head" style={{ marginBottom: 14 }}>
        <button
          type="button"
          className="admin-btn admin-btn-primary admin-btn-sm"
          onClick={() => setCreateOpen(true)}
          disabled={noBranches}
          title={
            noBranches ? 'Add a branch before creating staff' : undefined
          }
        >
          Add staff
        </button>
      </div>

      {noBranches && (
        <div className="admin-pending-note" style={{ marginBottom: 14 }}>
          Staff must belong to a branch. Add a branch in the{' '}
          <strong>Branches</strong> tab first, then come back here to add
          staff.
        </div>
      )}

      {staff.isLoading ? (
        <p className="admin-audit-meta">Loading staff…</p>
      ) : staff.isError ? (
        <p className="admin-audit-meta">Couldn't load staff.</p>
      ) : rows.length === 0 ? (
        <div className="admin-card">
          <div className="admin-empty">No staff yet.</div>
        </div>
      ) : (
        <div className="admin-card" style={{ padding: 0 }}>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Branch</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isEditing = editingId === row.id
                  return (
                    <tr key={row.id} style={{ cursor: 'default' }}>
                      <td data-label="Name">
                        {isEditing ? (
                          <input
                            className="admin-input"
                            value={editName}
                            onChange={(e) =>
                              setEditName(e.target.value)
                            }
                          />
                        ) : (
                          <>
                            {row.display_name}
                            {row.must_change_password && (
                              <span
                                className="admin-badge admin-badge-warn"
                                style={{ marginLeft: 6 }}
                              >
                                Temp pw
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td
                        className="admin-audit-meta"
                        data-label="Username"
                      >
                        {row.email ?? row.username ?? '—'}
                      </td>
                      <td data-label="Branch">
                        {isEditing ? (
                          <select
                            className="admin-select"
                            value={editBranch}
                            onChange={(e) =>
                              setEditBranch(e.target.value)
                            }
                          >
                            {branchOptions.map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          branchName(row.branch_id)
                        )}
                      </td>
                      <td
                        className="admin-audit-meta"
                        data-label="Created"
                      >
                        {fmtDate(row.created_at)}
                      </td>
                      <td data-label="Actions">
                        <div className="admin-row-actions">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm admin-btn-primary"
                                onClick={saveEdit}
                                disabled={patchStaff.isPending}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => setEditingId(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => startEdit(row)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => handleReset(row)}
                                disabled={resetPassword.isPending}
                              >
                                Reset password
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && (
        <Modal title="Add staff" onClose={() => setCreateOpen(false)}>
          <div className="admin-field">
            <label className="admin-label" htmlFor="staff-email">
              Email
            </label>
            <input
              id="staff-email"
              className="admin-input"
              type="email"
              autoComplete="off"
              autoCapitalize="none"
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
            />
            <p className="admin-hint">
              The staff member uses this email to log in at /staff/login.
            </p>
          </div>
          <div className="admin-field">
            <label className="admin-label" htmlFor="staff-name">
              Display name
            </label>
            <input
              id="staff-name"
              className="admin-input"
              value={form.display_name}
              onChange={(e) =>
                setForm({ ...form, display_name: e.target.value })
              }
            />
          </div>
          <div className="admin-field">
            <label className="admin-label" htmlFor="staff-branch">
              Branch
            </label>
            <select
              id="staff-branch"
              className="admin-select"
              value={form.branch_id}
              onChange={(e) =>
                setForm({ ...form, branch_id: e.target.value })
              }
            >
              <option value="">Select a branch…</option>
              {branchOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-field">
            <label className="admin-label" htmlFor="staff-password">
              Initial password
            </label>
            <input
              id="staff-password"
              className="admin-input"
              type="text"
              value={form.password}
              onChange={(e) =>
                setForm({ ...form, password: e.target.value })
              }
            />
            <p className="admin-hint">
              At least 12 characters. Relay it to the staff member; they can
              change it after first login.
            </p>
          </div>
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={handleCreate}
              disabled={createStaff.isPending}
            >
              {createStaff.isPending ? 'Creating…' : 'Create staff'}
            </button>
            <button
              type="button"
              className="admin-btn"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {resetResult && (
        <Modal
          title="Temporary password"
          onClose={() => setResetResult(null)}
          maxWidth={420}
        >
          <p style={{ marginTop: 0 }}>
            New temporary password for <strong>{resetResult.name}</strong>.
            They must change it on next login. This is shown once — copy it
            now.
          </p>
          <div className="admin-secret">{resetResult.password}</div>
          <div className="admin-form-actions">
            <CopyButton
              value={resetResult.password}
              label="Copy password"
            />
            <button
              type="button"
              className="admin-btn"
              onClick={() => setResetResult(null)}
            >
              Done
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
